import { motion } from 'framer-motion';
import { ShoppingCart, Wrench, FileText, Settings, Phone, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Services = () => {
  const services = [
    {
      icon: <ShoppingCart className="w-12 h-12" />,
      title: 'Generator Sales',
      description:
        'We offer a comprehensive range of diesel generators from leading manufacturers, covering capacities from 5 kVA to 250 kVA. Our experts help you select the perfect generator based on your power requirements, budget, and application.',
      features: [
        'Wide range of capacities (5-250 kVA)',
        'Premium quality generators from trusted brands',
        'Customized solutions for specific needs',
        'Competitive pricing and flexible payment options',
        'Comprehensive warranty coverage',
      ],
      image: 'https://images.unsplash.com/photo-1765897569589-effcccec14d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yJTIwcG93ZXIlMjBwbGFudCUyMHNpdGV8ZW58MHx8fHwxNzcxOTEzMjM2fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: 'Installation & Commissioning',
      description:
        'Our certified technicians provide professional installation and commissioning services to ensure your generator operates at peak efficiency from day one. We handle everything from site preparation to final testing.',
      features: [
        'Site survey and assessment',
        'Professional installation by certified engineers',
        'Load testing and performance verification',
        'Complete documentation and handover',
        'Training for your operational staff',
      ],
      image: 'https://images.unsplash.com/photo-1601119462238-d7f347e41844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwaW5kdXN0cmlhbCUyMGdlbmVyYXRvciUyMHNhZmV0eSUyMGdlYXJ8ZW58MHx8fHwxNzcxOTEzMjM3fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: 'Annual Maintenance Contracts (AMC)',
      description:
        'Keep your generator in optimal condition with our comprehensive AMC plans. Regular maintenance prevents breakdowns and extends the life of your equipment, ensuring it is ready when you need it most.',
      features: [
        'Scheduled preventive maintenance visits',
        'Oil and filter changes',
        'Battery testing and replacement',
        'Cooling system maintenance',
        'Performance monitoring and reporting',
        'Priority service response',
      ],
      image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    {
      icon: <Settings className="w-12 h-12" />,
      title: 'Repair & Troubleshooting',
      description:
        'Experiencing issues with your generator? Our experienced technicians can diagnose and repair all types of generator problems quickly and efficiently, minimizing your downtime.',
      features: [
        'Expert diagnosis of generator issues',
        'Genuine spare parts and components',
        'Quick turnaround time',
        'On-site and workshop repair services',
        'Post-repair testing and warranty',
      ],
      image: 'https://images.unsplash.com/photo-1601119462238-d7f347e41844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwaW5kdXN0cmlhbCUyMGdlbmVyYXRvciUyMHNhZmV0eSUyMGdlYXJ8ZW58MHx8fHwxNzcxOTEzMjM3fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      icon: <Phone className="w-12 h-12" />,
      title: 'Emergency Support 24/7',
      description:
        'Power emergencies do not follow a schedule. That is why we offer round-the-clock emergency support to address critical issues whenever they arise. Our rapid response team is always ready to help.',
      features: [
        '24/7 helpline availability',
        'Rapid response team',
        'On-site emergency repairs',
        'Remote troubleshooting support',
        'Critical spare parts inventory',
      ],
      image: 'https://images.unsplash.com/photo-1765897569589-effcccec14d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yJTIwcG93ZXIlMjBwbGFudCUyMHNpdGV8ZW58MHx8fHwxNzcxOTEzMjM2fDA&ixlib=rb-4.1.0&q=85',
    },
  ];

  return (
    <div className="pt-20" data-testid="services-page">
      <section className="py-20 bg-gradient-to-br from-[#1B365D] to-[#2563eb] text-white noise-overlay" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="services-title">
              Our Services
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto">
              Comprehensive diesel generator solutions from sales to after-sales support
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-section-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="text-[#F97316] mb-4">{service.icon}</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-4" data-testid={`service-title-${index}`}>
                    {service.title}
                  </h2>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#F97316] flex-shrink-0 mt-1" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 px-8 rounded-sm transition-all uppercase tracking-wide inline-block"
                    data-testid={`service-cta-${index}`}
                  >
                    Get This Service
                  </Link>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative rounded-sm overflow-hidden shadow-2xl">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1B365D] text-white noise-overlay" data-testid="service-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" data-testid="process-title">
              Our Service Process
            </h2>
            <p className="text-lg text-slate-300">A streamlined approach to delivering excellence</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', description: 'Understand your power requirements' },
              { step: '02', title: 'Planning', description: 'Design the optimal solution' },
              { step: '03', title: 'Implementation', description: 'Professional installation and setup' },
              { step: '04', title: 'Support', description: 'Ongoing maintenance and assistance' },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
                data-testid={`process-step-${index}`}
              >
                <div className="text-5xl font-bold text-[#F97316] mb-4">{item.step}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="service-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-6" data-testid="service-cta-title">
              Need a Custom Solution?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Our team is ready to design a tailored power solution that meets your unique requirements.
            </p>
            <Link
              to="/contact"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-4 px-10 rounded-sm transition-all uppercase tracking-wide inline-block"
              data-testid="service-cta-button"
            >
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};